import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import type {
  CreateTagResultDto,
  KbFilters,
  KbProfile,
  KbTagsListDto,
  KbThreadDetailDto,
  KbThreadsListDto,
} from '@/api/kb-client';
import { useKbAdminClient, useKbClient } from '@/api/kb-client-context';

// =============================================================================
// use-kb.ts — Hooks TanStack Query/Mutation de la KNOWLEDGE BASE (R15-COM-04).
// =============================================================================
// La CONSULTATION (liste/recherche + détail) est en useQuery, doublable par profil. Les MUTATIONS
// (taxonomie + assignation fil↔tag) sont ADMIN-ONLY (useKbAdminClient) : au succès, elles INVALIDENT
// la taxonomie ET/OU le détail du fil concerné (le contenu affiché est TOUJOURS re-lu par REST). ZÉRO
// règle métier ici : l'autorité (RLS, FTS, scope admin/fournisseur) est l'API.

/** Clé de query de la liste des fils archivés (profil + filtres de vue). */
export function kbThreadsKey(
  profile: KbProfile,
  filters: KbFilters,
): readonly unknown[] {
  return ['kb-threads', profile, filters];
}

/** Clé de query du détail d'un fil archivé (profil + threadId). */
export function kbThreadDetailKey(
  profile: KbProfile,
  threadId: string,
): readonly unknown[] {
  return ['kb-thread-detail', profile, threadId];
}

/** Clé de query de la taxonomie KB (admin-scope). */
export function kbTagsKey(): readonly unknown[] {
  return ['kb-tags'];
}

/** GET liste/recherche des fils archivés (filtrable). Client effectif = seam DI du profil (doublable). */
export function useKbThreadsQuery(
  profile: KbProfile,
  filters: KbFilters,
): UseQueryResult<KbThreadsListDto, Error> {
  const client = useKbClient(profile);
  return useQuery<KbThreadsListDto, Error>({
    queryKey: kbThreadsKey(profile, filters),
    queryFn: () => client.listThreads(filters),
  });
}

/**
 * GET détail d'un fil archivé (LECTURE SEULE). Activée seulement quand un `threadId` est sélectionné
 * (état d'URL) : `enabled` évite l'appel tant qu'aucun fil n'est ouvert.
 */
export function useKbThreadDetailQuery(
  profile: KbProfile,
  threadId: string | null,
): UseQueryResult<KbThreadDetailDto, Error> {
  const client = useKbClient(profile);
  return useQuery<KbThreadDetailDto, Error>({
    queryKey: kbThreadDetailKey(profile, threadId ?? ''),
    queryFn: () => client.getThreadDetail(threadId as string),
    enabled: threadId !== null && threadId.length > 0,
  });
}

/** GET taxonomie KB (ADMIN-ONLY). */
export function useKbTagsQuery(): UseQueryResult<KbTagsListDto, Error> {
  const client = useKbAdminClient();
  return useQuery<KbTagsListDto, Error>({
    queryKey: kbTagsKey(),
    queryFn: () => client.listTags(),
  });
}

/** POST création d'un tag (ADMIN). Au succès, INVALIDE la taxonomie (re-lue par REST). */
export function useCreateTag(): UseMutationResult<
  CreateTagResultDto,
  Error,
  { label: string; parentTagId?: string }
> {
  const client = useKbAdminClient();
  const queryClient = useQueryClient();
  return useMutation<
    CreateTagResultDto,
    Error,
    { label: string; parentTagId?: string }
  >({
    mutationFn: (input) => client.createTag(input.label, input.parentTagId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: kbTagsKey() });
    },
  });
}

/** DELETE suppression d'un tag (ADMIN). Au succès, INVALIDE la taxonomie ET les détails de fils. */
export function useDeleteTag(): UseMutationResult<void, Error, string> {
  const client = useKbAdminClient();
  const queryClient = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (tagId) => client.deleteTag(tagId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: kbTagsKey() });
      void queryClient.invalidateQueries({ queryKey: ['kb-thread-detail'] });
      void queryClient.invalidateQueries({ queryKey: ['kb-threads'] });
    },
  });
}

/**
 * POST/DELETE assignation d'un tag à un fil (ADMIN). Au succès, INVALIDE le détail du fil ET la liste
 * (les badges de tags de la liste reflètent l'assignation). Le contenu est re-lu par REST.
 */
export function useAssignTag(
  threadId: string,
): UseMutationResult<void, Error, string> {
  const client = useKbAdminClient();
  const queryClient = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (tagId) => client.assignTag(threadId, tagId),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: kbThreadDetailKey('admin', threadId),
      });
      void queryClient.invalidateQueries({ queryKey: ['kb-threads'] });
    },
  });
}

export function useUnassignTag(
  threadId: string,
): UseMutationResult<void, Error, string> {
  const client = useKbAdminClient();
  const queryClient = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (tagId) => client.unassignTag(threadId, tagId),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: kbThreadDetailKey('admin', threadId),
      });
      void queryClient.invalidateQueries({ queryKey: ['kb-threads'] });
    },
  });
}
